import { useMemo } from "react";
import { ISensorMeasuredData } from "types/ISensorMeasuredData";
import {
  Coordinate,
  calculateDistance,
} from "util/getCoordinatesWithinRadius/getCoordinatesWithinRadius";
import { validatePointInsidePolygon } from "util/validadePointInsidePolygon/validatePointInsidePolygon";

const SUGGESTION_RADIUS_KM = 30;

/**
 * Calculate minimum distance from a point to a polygon edge (perimeter).
 * For each segment of the polygon, compute the perpendicular distance from the point.
 */
function distanceToPolygonPerimeter(
  point: Coordinate,
  polygon: { lat: number; lng: number }[]
): number {
  let minDist = Infinity;

  for (let i = 0; i < polygon.length; i++) {
    const a = { latitude: polygon[i].lat, longitude: polygon[i].lng };
    const b = {
      latitude: polygon[(i + 1) % polygon.length].lat,
      longitude: polygon[(i + 1) % polygon.length].lng,
    };

    const dist = distanceToSegment(point, a, b);
    if (dist < minDist) {
      minDist = dist;
    }
  }

  return minDist;
}

/**
 * Calculate distance from a point to a line segment (great circle approximation).
 * Projects the point onto the segment and clamps to endpoints.
 */
function distanceToSegment(
  point: Coordinate,
  segA: Coordinate,
  segB: Coordinate
): number {
  const dAB = calculateDistance(segA, segB);
  if (dAB === 0) return calculateDistance(point, segA);

  // Project point onto the line defined by segA-segB using dot product approximation
  const dx = segB.latitude - segA.latitude;
  const dy = segB.longitude - segA.longitude;
  const px = point.latitude - segA.latitude;
  const py = point.longitude - segA.longitude;

  let t = (px * dx + py * dy) / (dx * dx + dy * dy);
  t = Math.max(0, Math.min(1, t));

  const closest: Coordinate = {
    latitude: segA.latitude + t * dx,
    longitude: segA.longitude + t * dy,
  };

  return calculateDistance(point, closest);
}

function sensorToCoordinate(sensor: ISensorMeasuredData): Coordinate {
  return {
    latitude: parseFloat(sensor.station_lat),
    longitude: parseFloat(sensor.station_lon),
  };
}

/**
 * Convert geofence {lat, lng}[] to turf polygon format [lng, lat][][]
 */
function geofenceToTurfPolygon(
  geofence: { lat: number; lng: number }[]
): number[][][] {
  const ring = geofence.map((p) => [p.lng, p.lat]);
  // Close the ring if not already closed
  if (
    ring.length > 0 &&
    (ring[0][0] !== ring[ring.length - 1][0] ||
      ring[0][1] !== ring[ring.length - 1][1])
  ) {
    ring.push([...ring[0]]);
  }
  return [ring];
}

interface UseSensorSuggestionParams {
  locationPoint?: { lat: number; lng: number };
  geofence?: { lat: number; lng: number }[];
  sensors: ISensorMeasuredData[];
}

interface UseSensorSuggestionResult {
  suggestedSensors: ISensorMeasuredData[];
  radiusKm: number;
}

export function useSensorSuggestion({
  locationPoint,
  geofence,
  sensors,
}: UseSensorSuggestionParams): UseSensorSuggestionResult {
  const suggestedSensors = useMemo(() => {
    if (!sensors || sensors.length === 0) return [];

    // Point location: calculate distance from point center
    if (locationPoint) {
      const center: Coordinate = {
        latitude: locationPoint.lat,
        longitude: locationPoint.lng,
      };

      return sensors.filter((sensor) => {
        const sensorCoord = sensorToCoordinate(sensor);
        const distance = calculateDistance(center, sensorCoord);
        return distance <= SUGGESTION_RADIUS_KM;
      });
    }

    // Area location (geofence): calculate minimum distance to polygon perimeter
    if (geofence && geofence.length >= 3) {
      const turfPolygon = geofenceToTurfPolygon(geofence);

      return sensors.filter((sensor) => {
        const sensorCoord = sensorToCoordinate(sensor);

        // Check if sensor is inside the polygon using turf.js
        if (
          validatePointInsidePolygon(
            [sensorCoord.longitude, sensorCoord.latitude],
            turfPolygon,
          )
        ) {
          return true;
        }

        // Calculate distance to nearest edge
        const dist = distanceToPolygonPerimeter(sensorCoord, geofence);
        return dist <= SUGGESTION_RADIUS_KM;
      });
    }

    return [];
  }, [locationPoint, geofence, sensors]);

  return {
    suggestedSensors,
    radiusKm: SUGGESTION_RADIUS_KM,
  };
}
